import "./index.scss";

import { Box, Modal, Stack, TextField } from "@mui/material";
import { ReactComponent as IconInfoCircle } from "assets/images/icons/attention-blue.svg";
import { ReactComponent as IconWarningTriangle } from "assets/images/icons/warning-triangle.svg";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import Button from "components/Button";
import ScreenLoader from "components/ScreenLoader";
import { ReactNode, useEffect, useState } from "react";

const ConfirmationBox = (props: {
  open: boolean;
  onClose: any;
  mainTitle?: string;
  title: string;
  message: ReactNode;
  type: "confirmation" | "alert" | "info";
  isLoading?: boolean;
  noButtonText?: string;
  yesButtonText?: string;
  specialNoValue?: string | boolean;
  initialInputValue?: string;
  initialInputValueAlt?: string;
}) => {
  const {
    open,
    onClose,
    mainTitle,
    title,
    message,
    type,
    isLoading,
    initialInputValue = "",
    initialInputValueAlt = "",
    noButtonText = "No",
    yesButtonText = "Yes",
    specialNoValue = false,
  } = props;
  const [loading, setLoading] = useState(false);

  const [inputValue, setInputValue] = useState(initialInputValue);
  const [inputValueAlt, setInputValueAlt] = useState(initialInputValueAlt);
  const [okDisabled, setOkDisabled] = useState(false);

  useEffect(() => {
    if (typeof isLoading === "boolean") {
      setLoading(isLoading);
    }
  }, [isLoading]);

  useEffect(() => {
    setInputValue(initialInputValue);
  }, [initialInputValue]);

  useEffect(() => {
    setInputValueAlt(initialInputValueAlt);
  }, [initialInputValueAlt]);

  const handleChangeInputValue = (e) => {
    setInputValue(e.target.value);
    setOkDisabled(!e.target.validity.valid);
  };

  //#region event handlers
  const handleCloseModal = (isConfirmed) => {
    if (typeof isConfirmed === "string" && specialNoValue) {
      onClose(specialNoValue);
    } else if (typeof isConfirmed === "boolean") {
      if (isConfirmed && initialInputValue && initialInputValueAlt) {
        onClose(inputValue, inputValueAlt);
      } else {
        onClose(isConfirmed);
      }
    } else {
      onClose();
    }
  };
  //#endregion event handlers

  return (
    <Modal
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
      }}
      open={open}
      onClose={handleCloseModal}
    >
      <Stack className="personal-details-modal-container confirmationBox">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          marginBottom="40px"
          className="header"
        >
          <h1>{mainTitle ? mainTitle : type === "confirmation" ? `Confirmation` : `Alert`}</h1>
          <DeleteIcon width="24px" height="24px" onClick={handleCloseModal} />
        </Stack>
        {!loading && (
          <>
            <Stack direction={"row"} flexGrow="1" gap={3}>
              {type === "info" ? <IconInfoCircle /> : <IconWarningTriangle />}
              <Stack>
                <Box className="title">{title}</Box>
                <Box className="description">{message}</Box>
              </Stack>
            </Stack>
            <Stack marginTop="24px">
              {initialInputValueAlt && (
                <TextField
                  type="text"
                  value={inputValueAlt}
                  onChange={(e) => setInputValueAlt(e.target.value)}
                />
              )}
            </Stack>
            <Stack marginTop="24px">
              {initialInputValue && (
                <TextField
                  type="email"
                  value={inputValue}
                  onChange={handleChangeInputValue}
                  error={okDisabled}
                  helperText={okDisabled ? "Please enter a valid email" : ""}
                />
              )}
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" marginTop="30px">
              <Stack direction="row" alignItems="center" spacing={3}>
                {type === "confirmation" && (
                  <>
                    <Button
                      label={noButtonText}
                      type="button"
                      className="text-button"
                      onClick={() => handleCloseModal(specialNoValue)}
                    />
                    <Button
                      label={yesButtonText}
                      type="button"
                      className="confirm-button"
                      onClick={() => handleCloseModal(true)}
                      disabled={okDisabled}
                    />
                  </>
                )}
                {(type === "alert" || type === "info") && (
                  <Button
                    label="Ok"
                    type="button"
                    className="confirm-button"
                    onClick={() => handleCloseModal(true)}
                  />
                )}
              </Stack>
            </Stack>
          </>
        )}
        {loading && (
          <Box mt="30px">
            <ScreenLoader isScreenLoader={true} />
          </Box>
        )}
      </Stack>
    </Modal>
  );
};

export default ConfirmationBox;
